import {FC, useContext, useEffect, useState} from 'react';
import Modal from '@atomaro/ui-kit/components/Modal/Modal';
import {
  CreateApiaryMapModalBody,
  CreateApiaryMapModalMarker,
  CreateApiaryModalHead,
  CreateApiaryModalTitle,
  CreateApiaryModalWrapper,
} from 'features/apiary/CreateApiary/styles.ts';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {CloseIcon} from 'shared/ui/icons';
import {toRem} from 'shared/helpers';
import {COLORS} from 'shared/consts';
import {ConfigContext} from '../../../app/App.tsx';
import {useReactify} from 'shared/api';
import {useHivesStore} from 'entities/hives';
import {HiveMapModalFooter} from 'widgets/hiveLocationModal/ui/styles.ts';
import {useParams} from 'react-router-dom';

type ApiaryLocationModalProps = {
  onClose: () => void;
  isMobile: boolean;
};

export const HiveLocationModal: FC<ApiaryLocationModalProps> = ({isMobile, onClose}) => {
  const {id} = useParams<{id: string}>();
  const {VITE_YANDEX_TOKEN} = useContext(ConfigContext);
  const reactify = useReactify(VITE_YANDEX_TOKEN);
  const isLocationModalOpen = useHivesStore((s) => s.isLocationModalOpen);
  const getHiveLocation = useHivesStore((s) => s.getHiveLocation);

  const fallbackCenter: [number, number] = [55.756736, 37.613997];
  const safeZoom = 10;

  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [safeCenter, setSafeCenter] = useState<[number, number]>(fallbackCenter);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const res = await getHiveLocation(id);
      if (res?.lat && res.lon) {
        const localCoords: [number, number] = [res.lon, res.lat];
        setCoordinates(localCoords);
        setSafeCenter(localCoords);
      }
    };

    load();
  }, [id]);

  if (!reactify) return null;

  return (
    <Modal
      isOpen={isLocationModalOpen}
      centered
      style={{padding: 0, maxHeight: '100%'}}
      onClickOverlay={onClose}
    >
      <CreateApiaryModalWrapper $minWidth={640}>
        <CreateApiaryModalHead>
          <CreateApiaryModalTitle>Координаты улья</CreateApiaryModalTitle>
          <Button
            iconPrefix={<CloseIcon />}
            variant="ghost"
            colorScheme="neutral"
            style={{width: toRem(24)}}
            size={isMobile ? 's' : 'm'}
            onClick={onClose}
          />
        </CreateApiaryModalHead>
        <CreateApiaryMapModalBody>
          <reactify.YMap
            location={{zoom: safeZoom, center: safeCenter}}
            className="create-apiary-map"
          >
            <reactify.YMapDefaultSchemeLayer />
            <reactify.YMapDefaultFeaturesLayer />

            {coordinates ? (
              <reactify.YMapMarker coordinates={coordinates}>
                <CreateApiaryMapModalMarker>
                  <svg
                    width="35"
                    height="40"
                    viewBox="0 0 35 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.505 0.548624C18.2646 -0.182835 16.7354 -0.182878 15.495 0.548515L2.0056 8.49989C0.764582 9.23138 0 10.5833 0 12.0464V27.9524C0 29.4154 0.764587 30.7672 2.005 31.4987L15.495 39.4514C16.7354 40.1829 18.2646 40.1829 19.505 39.4514L32.995 31.4987C34.2354 30.7672 35 29.4154 35 27.9524V12.0475C35 10.5845 34.2354 9.23261 32.995 8.50112L19.505 0.548624Z"
                      fill={COLORS.ACCENT.DEFAULT}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.6929 10.9598C17.8829 10.8812 18.0999 10.8941 18.2809 10.9988L22.4316 13.4012C22.6412 13.5225 22.7706 13.7487 22.7707 13.9933V18.4029L26.5822 20.6091C26.7919 20.7305 26.9213 20.9566 26.9214 21.2012V26.0068C26.9213 26.2514 26.7918 26.4776 26.5822 26.599L22.4316 29.0013C22.2247 29.121 21.9706 29.121 21.7637 29.0013L17.947 26.7916L14.1303 29.0013C13.9234 29.121 13.6693 29.121 13.4625 29.0013L9.31182 26.599C9.1022 26.4776 8.97276 26.2514 8.97266 26.0068V21.2012L8.97879 21.1107C9.00643 20.9019 9.12833 20.7153 9.31182 20.6091L13.1233 18.4029V13.9933C13.1234 13.7487 13.2528 13.5225 13.4625 13.4012L17.6131 10.9988L17.6929 10.9598ZM10.3188 21.5963V25.6109L13.7964 27.6235L17.2739 25.6109V21.5963L13.7964 19.5837L10.3188 21.5963ZM18.6201 21.5963V25.6109L22.0977 27.6235L25.5752 25.6109V21.5963L22.0977 19.5837L18.6201 21.5963ZM14.4695 14.3884V18.4029L17.947 20.4155L21.4246 18.4029V14.3884L17.947 12.3758L14.4695 14.3884Z"
                      fill={COLORS.STATIC.WHITE}
                    />
                  </svg>
                </CreateApiaryMapModalMarker>
              </reactify.YMapMarker>
            ) : (
              <p>Координаты улья не синхронизированы</p>
            )}

            <reactify.YMapControls position="right" orientation="vertical">
              <reactify.YMapZoomControl />
              <reactify.YMapGeolocationControl />
            </reactify.YMapControls>
          </reactify.YMap>
        </CreateApiaryMapModalBody>
        {Boolean(coordinates) && (
          <HiveMapModalFooter>
            <b>Точка на карте</b>
            <p>
              <span>Координаты</span>
              <span>{coordinates?.join(', ')}</span>
            </p>
          </HiveMapModalFooter>
        )}
      </CreateApiaryModalWrapper>
    </Modal>
  );
};
